// flux-select.js — Flux-themed custom dropdown widget that wraps a
// standard <select> element. The underlying <select> stays in the DOM
// (hidden) so existing reads (`select.value`, change events, admin
// gating that toggles `hidden`/`disabled` on <option>) keep working.
//
// Opt in by adding class="flux-select" to any <select> in the markup.
// The widget auto-wires on DOMContentLoaded. Mutations on <option>
// hidden/disabled are observed, so admin-gating changes (e.g. BSSN
// grid-resolution admin-only entries) propagate into the menu.

function initFluxSelect(select) {
  if (select._fluxBound) return;
  if (!select.parentNode) return;
  select._fluxBound = true;

  const wrap = document.createElement('span');
  wrap.className = 'flux-select-wrap';

  const trigger = document.createElement('button');
  trigger.type = 'button';
  trigger.className = 'flux-select-trigger';
  trigger.setAttribute('aria-haspopup', 'listbox');
  trigger.setAttribute('aria-expanded', 'false');

  const triggerLabel = document.createElement('span');
  triggerLabel.className = 'flux-select-label';

  const caret = document.createElement('i');
  caret.className = 'fas fa-caret-down flux-select-caret';
  caret.setAttribute('aria-hidden', 'true');

  trigger.appendChild(triggerLabel);
  trigger.appendChild(caret);

  const menu = document.createElement('div');
  menu.className = 'flux-select-menu hidden';
  menu.setAttribute('role', 'listbox');

  // Place wrap before the select, then move the select inside so the
  // native element remains under the same parent for form lookups.
  select.parentNode.insertBefore(wrap, select);
  wrap.appendChild(select);
  wrap.appendChild(trigger);
  wrap.appendChild(menu);
  select.classList.add('flux-select-native');

  function buildMenu() {
    menu.innerHTML = '';
    Array.from(select.children).forEach(child => {
      if (child.tagName === 'OPTGROUP') {
        if (child.hidden) return;
        const visibleOpts = Array.from(child.children).filter(o => !o.hidden);
        if (visibleOpts.length === 0) return;
        const label = document.createElement('div');
        label.className = 'flux-select-group';
        label.textContent = child.label || '';
        menu.appendChild(label);
        visibleOpts.forEach(opt => addOption(opt));
      } else if (child.tagName === 'OPTION') {
        addOption(child);
      }
    });
  }

  function addOption(opt) {
    if (opt.hidden) return;
    const el = document.createElement('div');
    el.className = 'flux-select-option';
    el.setAttribute('role', 'option');
    el.dataset.value = opt.value;
    el.textContent = opt.textContent;
    if (opt.disabled) {
      el.classList.add('disabled');
      el.setAttribute('aria-disabled', 'true');
    } else {
      el.addEventListener('click', (e) => {
        e.stopPropagation();
        select.value = opt.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        updateLabel();
        close();
      });
    }
    if (opt.value === select.value) el.classList.add('selected');
    menu.appendChild(el);
  }

  function updateLabel() {
    const opt = select.selectedOptions[0];
    triggerLabel.textContent = opt ? opt.textContent : '';
    menu.querySelectorAll('.flux-select-option').forEach(el => {
      el.classList.toggle('selected', el.dataset.value === select.value);
    });
  }

  function open() {
    buildMenu();
    menu.classList.remove('hidden');
    trigger.setAttribute('aria-expanded', 'true');
    // Defer outside-click registration so the trigger's own click event
    // (which opened the menu) doesn't immediately close it.
    setTimeout(() => document.addEventListener('click', outsideClick, true), 0);
    document.addEventListener('keydown', escClose, true);
  }
  function close() {
    menu.classList.add('hidden');
    trigger.setAttribute('aria-expanded', 'false');
    document.removeEventListener('click', outsideClick, true);
    document.removeEventListener('keydown', escClose, true);
  }
  function outsideClick(e) { if (!wrap.contains(e.target)) close(); }
  function escClose(e) { if (e.key === 'Escape') close(); }

  trigger.addEventListener('click', (e) => {
    e.stopPropagation();
    if (menu.classList.contains('hidden')) open();
    else close();
  });

  // Keep label in sync when the underlying value is set programmatically.
  select.addEventListener('change', updateLabel);

  // Watch for admin-gating changes (`hidden`/`disabled` on options).
  const mo = new MutationObserver(() => {
    if (!menu.classList.contains('hidden')) buildMenu();
    updateLabel();
  });
  mo.observe(select, {
    attributes: true,
    subtree: true,
    attributeFilter: ['hidden', 'disabled', 'value'],
  });

  buildMenu();
  updateLabel();
}

export function wireFluxSelects(root = document) {
  root.querySelectorAll('select.flux-select').forEach(initFluxSelect);
}

// Auto-wire on DOMContentLoaded for any markup that ships with the class.
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => wireFluxSelects(document));
} else {
  wireFluxSelects(document);
}

// Expose for late-mounted views (e.g. after dynamic injection).
window.gdbsFluxSelectWire = wireFluxSelects;
